import { useState, useEffect, useRef } from 'react';
import { useContinuousPlayer } from '../context/ContinuousPlayerContext';
import { useAudioVoice } from '../context/AudioVoiceContext';
import { useReadingProgress } from './useReadingProgress';

export function useListenSession(variant = 'apple') {
  const { voice, setVoice } = useAudioVoice();
  const player = useContinuousPlayer();
  const { settings, startPlayback, queue, isLoading, currentIndex } = player;
  const { markRead } = useReadingProgress();
  const prevIndexRef = useRef(null);

  const [localSettings, setLocalSettings] = useState(settings);
  const hasQueue = queue.length > 0;

  // Auto-start with last settings on return visits.
  // Skip if another startPlayback call is already in flight (e.g. "Listen to chapter" CTA).
  useEffect(() => {
    if (isLoading || queue.length > 0) return;
    if (!localStorage.getItem('gita-player-settings')) return;
    startPlayback({ ...localSettings, variant });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // mount only

  // Mark the previous track as read whenever the playing index advances.
  useEffect(() => {
    if (queue.length === 0) return;
    const prev = prevIndexRef.current;
    prevIndexRef.current = currentIndex;
    if (prev !== null && prev !== currentIndex) {
      const prevTrack = queue[prev];
      if (prevTrack) markRead(prevTrack.chapterNumber, prevTrack.slokaNumber);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const updateLocal = (patch) => {
    setLocalSettings((prev) => ({ ...prev, ...patch }));
  };

  const handleStart = () => {
    startPlayback({ ...localSettings, variant });
  };

  return {
    voice,
    setVoice,
    localSettings,
    updateLocal,
    handleStart,
    hasQueue,
    ...player
  };
}
